import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useReducer, useState } from "react";
import {
  Link,
  type LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { invalidateCurrentOrderDetails } from "@/modules/cart/useCartDetails";
import { FormattedString } from "@/utils/FormattedStrings";
import { getGetAuthClaimsQueryOptions } from "@/api/orval/auth/auth";
import { createOrderLineItem, getListCartSummaryQueryKey } from "@/api/orval/cart/cart";
import {
  getGetListingPackagesQueryOptions,
  getGetListingQueryOptions,
} from "@/api/orval/listing/listing";
import {
  getGetPlatformLocationsQueryOptions,
  getPlatformGetPlatformQueryOptions,
} from "@/api/orval/platform/platform";
import {
  getGetPlatformLocationQueryOptions,
  getGetPlatformUserLocationQueryOptions,
} from "@/api/orval/platform-location/platform-location";
import {
  getGetPlatformUserPackageQueryOptions,
  redeemPlatformUserPackageItemInApp,
} from "@/api/orval/platform-user-package/platform-user-package";
import {
  getCheckIfUserCanCheckInQueryOptions,
  getGetUserInfoQueryOptions,
} from "@/api/orval/user/user";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import AddedToCartCheck from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import { ImageCarousel } from "atoms/ImageCarousel/ImageCarousel";
import { TextButton } from "atoms/text-button/TextButton";
import { AccordionList } from "molecules/accordion-list/AccordionList";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { AddedToCartModal } from "organisms/added-to-cart-modal/AddedToCartModal";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getBookAppointmentAction, getProductTitle } from "services/HelperService";
import { waitObj } from "toolbox/Promise";
import { openUrl } from "utils/openUrl/openUrl";
import pluralize from "utils/pluralize";

import { BuyAgainModal } from "./BuyAgainModal";
import { PackageItemCard } from "./PackageItemCard";
import { QuantitySelectorModal, type TreatmentProps } from "./QuantitySelectorModal";
import { TreatmentSelectorModal } from "./TreatmentSelectorModal";
import { invalidateRedeemedPackage } from "./treatmentQueries";

type Action =
  | { type: "package"; packageId: string | null; priceId: string | null }
  | { type: "price"; priceId: string }
  | { type: "open"; modal: "cart" }
  | { type: "close" };

type State = {
  packageId: string | null | undefined;
  priceId: string | null | undefined;
  modal: "cart" | null;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "package":
      return { ...state, packageId: action.packageId, priceId: action.priceId };

    case "price":
      return { ...state, priceId: action.priceId };

    case "open":
      return { ...state, modal: action.modal };

    case "close":
      return { ...state, modal: null };
  }
};

const load = async ({
  active,
  isFromMembership,
  comingFromScan,
  packageId,
  locationId,
}: {
  active: boolean;
  isFromMembership: boolean;
  comingFromScan: boolean;
  packageId: string;
  locationId?: string;
}) => {
  const platformLocation = queryClient.fetchQuery(getGetPlatformUserLocationQueryOptions());

  const platformUserPackage = queryClient.fetchQuery(
    getGetPlatformUserPackageQueryOptions(packageId, {
      isFromMembership,
      active,
      locationId: locationId ?? undefined,
    }),
  );

  const data = await waitObj({ platformUserPackage, platformLocation });

  const id = data.platformUserPackage.packageListingId;
  const platformUserPackageLocationId = data.platformUserPackage.locationId;
  let packageData = null;
  let listing = null;
  let platformUserPackageLocation = null;

  if (id) {
    packageData = queryClient.fetchQuery(
      getGetListingPackagesQueryOptions(id, { excludeLocation: true }),
    );

    listing = queryClient.fetchQuery(getGetListingQueryOptions(id, { excludeLocation: true }));
  }

  if (platformUserPackageLocationId) {
    platformUserPackageLocation = queryClient.fetchQuery(
      getGetPlatformLocationQueryOptions(platformUserPackageLocationId),
    );
  }

  const authClaims = queryClient.fetchQuery(getGetAuthClaimsQueryOptions());
  const platform = queryClient.fetchQuery(getPlatformGetPlatformQueryOptions());

  const isAdminImpersonation = authClaims.then((response) => !!response?.claims.adminUserId);

  const checkInAvailable = queryClient.fetchQuery(getCheckIfUserCanCheckInQueryOptions());

  const user = await queryClient.fetchQuery(getGetUserInfoQueryOptions());

  const locations = queryClient.ensureQueryData(getGetPlatformLocationsQueryOptions({}));

  return waitObj({
    platformUserPackage,
    platformLocation,
    platform,
    packageId,
    packageData,
    listing,
    locations,
    platformUserPackageLocation,
    comingFromScan,
    isAdminImpersonation,
    userLocation: user.platformLocation,
    checkInAvailable,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const treatmentLoader: LoaderFunction = async ({
  params: { packageId },
  request: { url },
}) => {
  if (!packageId) {
    return redirect("..");
  }

  const searchParams = new URL(url).searchParams;
  const active = searchParams.get("active") === "true";
  const isFromMembership = searchParams.get("isFromMembership") === "true";
  const comingFromScan = searchParams.get("comingFromScan") === "true";
  const locationIdQueryValue = searchParams.get("locationId");
  const locationId =
    locationIdQueryValue === null ||
    locationIdQueryValue === "null" ||
    locationIdQueryValue === "undefined"
      ? undefined
      : locationIdQueryValue;

  return await load({
    active,
    isFromMembership,
    comingFromScan,
    packageId,
    locationId,
  });
};

const singlePackage = (packageData: any) => (packageData.length === 1 ? packageData[0].id : null);

const singlePrice = (packageData: any, packageId: string | null) => {
  const $package = packageData.find((p: any) => p.id === packageId);
  return $package?.packagePrices?.length === 1 ? $package.packagePrices[0].id : null;
};

function resolveInitialPriceId(
  isPackageAvailable: boolean | null | undefined,
  previousSelectedAmount: { id: string } | null,
  packageData: any,
): string | null {
  if (isPackageAvailable && previousSelectedAmount) {
    return previousSelectedAmount.id;
  }
  if (isPackageAvailable) {
    return singlePrice(packageData, singlePackage(packageData));
  }
  return null;
}

/**
 * All types of modals that can be shown on the TreatmentPage.
 * Only one can be shown at a time.
 * Let's use a union type to represent them.
 */
type TreatmentPageModalType =
  | "quantity"
  | "buyAgain"
  | "location"
  | "treatmentSelector"
  | "treatmentQuantity"
  | "redeemSuccess"
  | null;

function processTreatmentSelection(
  models: { productId: string; name: string; remainingQuantity: number }[],
  id: string,
  onShowQuantity: (details: TreatmentProps) => void,
  onSelectOne: (details: TreatmentProps) => void,
) {
  const selected = models.find(({ productId }) => productId === id);
  const details: TreatmentProps = {
    id: selected?.productId ?? "",
    name: selected?.name ?? "",
    remainingQuantity: selected?.remainingQuantity ?? 0,
  };
  if (selected?.remainingQuantity && selected.remainingQuantity > 1) {
    onShowQuantity(details);
  } else {
    onSelectOne(details);
  }
}

function executeTreatmentQuantityAction(params: {
  quantity: number;
  skipScanInOffice: boolean;
  locationIdsLength: number | undefined;
  locationsItemsLength: number;
  isPackage: boolean;
  treatmentDetailsId: string;
  onRedeem: (quantity: number, productId?: string) => void;
  onShowLocation: (quantity: number) => void;
}) {
  if (params.skipScanInOffice) {
    if (params.locationIdsLength === 1 || params.locationsItemsLength === 1) {
      params.onRedeem(params.quantity, params.treatmentDetailsId);
      return;
    }
    params.onShowLocation(params.quantity);
  } else if (params.isPackage) {
    params.onRedeem(params.quantity, params.treatmentDetailsId);
  } else {
    params.onRedeem(params.quantity);
  }
}

function executeRedeemAction(
  isPackage: boolean,
  remainingQuantity: number,
  onShowQuantity: () => void,
  onSelectOne: () => void,
  onShowSelector: () => void,
) {
  if (!isPackage) {
    if (remainingQuantity > 1) {
      onShowQuantity();
    } else {
      onSelectOne();
    }
    return;
  }
  onShowSelector();
}

function executeRedeeming(
  productId: string | undefined,
  defaultProductId: string,
  skipScanInOffice: boolean,
  quantity: number,
  locationId: string | undefined,
  onMutate: (params: { quantity: number; productId: string; overrideLocationId?: string }) => void,
  onScanNavigate: (quantity: number, productId: string) => void,
) {
  const productIdInput = productId || defaultProductId;
  if (skipScanInOffice) {
    onMutate({
      quantity,
      productId: productIdInput,
      ...(locationId && { overrideLocationId: locationId }),
    });
  } else {
    onScanNavigate(quantity, productIdInput);
  }
}

function executeRedeemWithLocation(
  selectedQuantity: number | null,
  isPackage: boolean,
  treatmentDetailsId: string,
  locationId: string,
  onRedeem: (quantity: number, productId: string | undefined, locationId: string) => void,
  onCleanup: () => void,
) {
  if (selectedQuantity === null) return;
  if (isPackage) {
    onRedeem(selectedQuantity, treatmentDetailsId, locationId);
  } else {
    onRedeem(selectedQuantity, undefined, locationId);
  }
  onCleanup();
}

export const TreatmentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    platformUserPackage,
    platformLocation,
    platform,
    packageData,
    packageId,
    listing,
    locations,
    platformUserPackageLocation,
    comingFromScan,
    isAdminImpersonation,
    userLocation,
    checkInAvailable,
  } = useLoaderData() as LoaderData;

  const [activeModal, setActiveModal] = useState<TreatmentPageModalType>(null);
  const hideActiveModal = () => setActiveModal(null);

  const locationsItems = locations.items ?? [];
  const isAllLocations = listing?.locationIds?.length === locationsItems.length;
  const isPatientLocationDifferentFromListing = userLocation?.id
    ? !listing?.locationIds?.includes(userLocation.id)
    : true;

  const locationDisplayText =
    listing?.locationIds
      ?.map((id) => locationsItems.find((loc) => loc.id === id)?.name)
      .filter(Boolean)
      .join(", ") ||
    userLocation?.name ||
    t("treatmentPage.locationFallback");

  const locationText = isAllLocations ? (
    <>
      {t("treatmentPage.redeemableAtPrefix")}{" "}
      <strong>{t("treatmentPage.allLocationsLabel")}</strong>
    </>
  ) : (
    <>
      {t("treatmentPage.redeemableAtTheLocationPrefix")} <strong>{locationDisplayText}</strong>
    </>
  );

  const isPackageAvailable = platformUserPackage.packageListingActive;
  const [searchParams, setSearchParams] = useSearchParams();
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const firstPackageItem = platformUserPackage.platformUserPackageItemModels[0];
  const [treatmentDetails, setTreatmentDetails] = useState<TreatmentProps>({
    id: firstPackageItem?.productId ?? "",
    name: firstPackageItem?.name ?? "",
    remainingQuantity: firstPackageItem?.remainingQuantity ?? 0,
  });

  const manualRedemptionOnly = platform.platformSettings?.manualRedemptionOnlyEnabled ?? false;
  const restrictRedemptionsToPackageLocation =
    platform.platformSettings?.restrictPatientRedemptionsToPackageLocation ?? false;
  const skipScanInOffice =
    isAdminImpersonation || (!restrictRedemptionsToPackageLocation && !checkInAvailable);

  const treatmentSelectorModalButtonText = skipScanInOffice
    ? t("treatmentPage.redeemNow")
    : t("common.next");
  const $package = packageData?.find(({ id }) => id === packageId);
  let previousSelectedAmount =
    $package?.packagePrices.find(
      ({ quantity }) => quantity === platformUserPackage.totalTreatmentAmount,
    ) || null;

  const initialPriceId = resolveInitialPriceId(
    isPackageAvailable,
    previousSelectedAmount,
    packageData,
  );

  const [state, dispatch] = useReducer(reducer, {
    packageId: isPackageAvailable ? singlePackage(packageData) : null,
    priceId: initialPriceId,
    modal: null,
  });

  const price = $package?.packagePrices.find(({ id }) => id === state.priceId) || null;
  const cost = price?.price ?? $package?.baseCost ?? (listing?.baseCost || null);

  // only relevant when admins are impersonating, ENG-2038
  // https://app.asana.com/1/1200167319116526/project/1209918357392839/task/1210160254752052
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  useEffect(() => {
    if (comingFromScan) {
      setActiveModal("redeemSuccess");
    }
  }, [comingFromScan]);

  const closeRedeemSuccessModal = () => {
    setActiveModal(null);
    if (searchParams.has("comingFromScan")) {
      searchParams.delete("comingFromScan");
      setSearchParams(searchParams);
    }
  };

  const { bookAppointmentUrl, phoneNumber, email } =
    platformUserPackageLocation ?? platformLocation;

  const bookAppointmentHref = getBookAppointmentAction(bookAppointmentUrl, phoneNumber, email);

  const isPackage = platformUserPackage.platformUserPackageItemModels.length > 1;

  const handleTreatmentSelectorOnClick = (id: string) => {
    setActiveModal(null);
    processTreatmentSelection(
      platformUserPackage.platformUserPackageItemModels,
      id,
      (details) => {
        setTreatmentDetails(details);
        setActiveModal("treatmentQuantity");
      },
      (details) => {
        setTreatmentDetails(details);
        handleTreatmentQuantityOnClick(1);
      },
    );
  };

  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);

  const handleTreatmentQuantityOnClick = (quantity: number) => {
    setActiveModal(null);
    executeTreatmentQuantityAction({
      quantity,
      skipScanInOffice,
      locationIdsLength: listing?.locationIds?.length,
      locationsItemsLength: locationsItems.length,
      isPackage,
      treatmentDetailsId: treatmentDetails.id,
      onRedeem: handleRedeeming,
      onShowLocation: (q) => {
        setSelectedQuantity(q);
        setActiveModal("location");
      },
    });
  };

  const isPreviousTreatment = platformUserPackage.remainingTreatmentAmount === 0;

  // Custom plans are one-time purchases and should not show "Buy again"
  const isCustomPlan = !!listing?.platformUserId;

  const handleRedeemOnClick = () => {
    executeRedeemAction(
      isPackage,
      firstPackageItem?.remainingQuantity ?? 0,
      () => setActiveModal("treatmentQuantity"),
      () => handleTreatmentQuantityOnClick(1),
      () => setActiveModal("treatmentSelector"),
    );
  };

  const handleRedeeming = (quantity: number, productId?: string, locationId?: string) => {
    executeRedeeming(
      productId,
      platformUserPackage.platformUserPackageItemModels[0].productId,
      skipScanInOffice,
      quantity,
      locationId,
      (params) => redeemMutation.mutate(params),
      handleScanToRedeemNavigation,
    );
  };

  const handleRedeemWithLocation = (locationId: string) => {
    executeRedeemWithLocation(
      selectedQuantity,
      isPackage,
      treatmentDetails.id,
      locationId,
      handleRedeeming,
      () => {
        setSelectedQuantity(null);
        setSelectedLocationId(null);
      },
    );
  };

  const isFromMembership = platformUserPackage.type === "MembershipTreatment";

  const handleScanToRedeemNavigation = (quantity: number, productId: string) => {
    navigate("/scan-redeem-treatment-in-location", {
      state: {
        packageId: platformUserPackage.packageId,
        isFromMembership: isFromMembership.toString(),
        productId,
        remainingAmount: platformUserPackage.remainingTreatmentAmount - quantity,
        quantity,
        locationId: platformUserPackage.locationId,
      },
    });
  };

  const redeemMutation = useMutation({
    mutationFn: ({
      quantity,
      productId,
      overrideLocationId,
    }: {
      quantity: number;
      productId: string;
      overrideLocationId?: string;
    }) =>
      redeemPlatformUserPackageItemInApp({
        packageId: platformUserPackage.packageId,
        locationId: overrideLocationId ?? platformUserPackage.locationId,
        isFromMembership,
        productId,
        quantity,
      }),
    onSuccess: async (_, variables) => {
      await invalidateRedeemedPackage(queryClient, packageId);

      const isPackageActive = !!(platformUserPackage.remainingTreatmentAmount - variables.quantity);
      navigate(
        `/wallet/treatments/${platformUserPackage.packageId}?isFromMembership=${isFromMembership}&active=${isPackageActive}&comingFromScan=true&locationId=${platformUserPackage.locationId}`,
      );
    },
  });
  const activeAndAvailable = listing?.active;

  const availableLocations = listing?.locationIds?.length
    ? locationsItems.filter((location) => listing.locationIds.includes(location.id))
    : locationsItems; // If no restrictions, show all locations

  const isPartnerEmrBookingEnabled = platform.platformSettings?.partnerEmrBookingEnabled;

  const isBookableService =
    platformUserPackage.platformUserPackageItemModels.length === 1 && isPartnerEmrBookingEnabled;

  const singularUnit = isPackage
    ? t("treatmentPage.unitSingular")
    : (platformUserPackage.packageUnitType?.singularUnitType ?? "");
  const pluralUnit = isPackage
    ? t("treatmentPage.unitPlural")
    : (platformUserPackage.packageUnitType?.pluralUnitType ?? "");
  const redeemButtonLabel = skipScanInOffice
    ? t("treatmentPage.redeemNow")
    : t("treatmentPage.scanInOfficeToRedeem");
  const bookButtonLabel = isUnitedDerm
    ? t("treatmentPage.requestAppointment")
    : t("treatmentPage.bookAppointment");
  const showBookAppointment = !isPartnerEmrBookingEnabled && !!bookAppointmentHref;
  const showBuyAgainSection = isPreviousTreatment && !isCustomPlan;
  const buyAgainButtonStyle = activeAndAvailable ? ("primary" as const) : ("secondary" as const);
  const buyAgainLabel = activeAndAvailable
    ? t("treatmentPage.buyAgain")
    : t("treatmentPage.browseShop");
  const buyAgainDestination = activeAndAvailable ? `/pdp/package/${listing?.id}` : "/shop";
  const quantityModalLabel = skipScanInOffice ? t("common.continue") : t("treatmentPage.scanNow");
  const productTitle = getProductTitle(
    platformUserPackage.productName,
    platformUserPackage.variantName,
  );
  const showDescription = !!listing?.description && !isPackage;
  const showRedeemButton = !platformUserPackage.packageZenotiId;
  const hasExpectations = !!platformUserPackage.expectations?.length;
  const showMoreInfoLink =
    !!platformUserPackage.packageListingId && !isPatientLocationDifferentFromListing;
  const remainingItems = platformUserPackage.platformUserPackageItemModels.filter(
    (item) => item.remainingQuantity > 0,
  );
  const showRemainingBreakdown = isPackage && platformUserPackage.remainingTreatmentAmount > 0;
  const availableTreatments = platformUserPackage.platformUserPackageItemModels
    .filter((item) => item.remainingQuantity > 0)
    .map((item) => ({ id: item.productId, name: item.name, quantity: item.remainingQuantity }));

  return (
    <div className="flex h-full flex-col bg-two">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate("/wallet/treatments");
        }}
      >
        <span className="flex items-center">
          <h1 className="text-xl">{t("treatmentPage.header")}</h1>
        </span>
      </TopNav>

      <div className="flex flex-1 flex-col overflow-y-auto bg-white">
        {/* Header Section */}
        <div className="flex flex-col items-center gap-2 pb-4 pt-6">
          <div className="text-center font-serif text-h2 text-primary">{productTitle}</div>
        </div>

        {/* Image Section */}
        {platformUserPackage.imageUrl && (
          <div className="relative z-0 flex flex-col items-center pb-2">
            <ImageCarousel
              slides={[{ src: platformUserPackage.imageUrl, alt: productTitle }]}
              productName={productTitle}
            />
          </div>
        )}

        {/* Info Section */}
        <div className="px-4">
          <div className="flex flex-col items-center pb-4">
            <div className="text-center text-body2 text-secondary">
              {t("treatmentPage.purchasedDate", {
                date: dayjs(platformUserPackage.purchasedDate).format("MMMM D, YYYY"),
              })}
            </div>
            <div className="text-center text-body2 text-secondary">{locationText}</div>
          </div>
        </div>

        {!isBookableService && (
          <div className="px-4">
            <div className="flex flex-col items-center pb-4">
              <div className="text-center text-bold1 text-primary">
                {t("treatmentPage.remainingCount", {
                  remaining: platformUserPackage.remainingTreatmentAmount,
                  total: platformUserPackage.totalTreatmentAmount,
                  noun: pluralize(
                    platformUserPackage.totalTreatmentAmount,
                    singularUnit,
                    pluralUnit,
                  ),
                })}
              </div>

              {showRemainingBreakdown && (
                <ul className="mt-2 list-none text-center text-body2 text-secondary">
                  {remainingItems.map(({ productId, name, remainingQuantity }) => (
                    <li key={productId}>
                      {t("treatmentPage.remainingItemLine", { quantity: remainingQuantity, name })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!isPreviousTreatment && (
          <div className="px-4 pb-4">
            {showRedeemButton &&
              (manualRedemptionOnly ? (
                <div className="mb-2 rounded bg-two px-3 py-2.5 text-center text-body2 text-primary">
                  {t("treatmentPage.manualRedemptionMessage", {
                    platformName: platform?.name ?? t("common.yourProvider"),
                  })}
                </div>
              ) : (
                <div className="mb-2">
                  <Button size="large" fullWidth onClick={handleRedeemOnClick}>
                    {!skipScanInOffice && <Icon size="xsmall" svg={QrCode} color="white" />}
                    <div className="ml-1.5">{redeemButtonLabel}</div>
                  </Button>
                </div>
              ))}
            {showBookAppointment && (
              <Button
                size="large"
                style="secondary"
                fullWidth
                onClick={() => openUrl(bookAppointmentHref)}
              >
                {bookButtonLabel}
              </Button>
            )}
          </div>
        )}

        {showBuyAgainSection && (
          <div className="px-4 pb-4">
            {!activeAndAvailable && (
              <div className="mb-4 text-center text-body2 text-secondary">
                {t("treatmentPage.serviceNoLongerAvailable")}
              </div>
            )}
            <Button
              size="large"
              style={buyAgainButtonStyle}
              fullWidth
              onClick={() => navigate(buyAgainDestination)}
            >
              {buyAgainLabel}
            </Button>
          </div>
        )}

        {/* Description Section - Only show for non-packages */}
        {showDescription && (
          <div className="p-4">
            <div className="text-h4 mb-2 font-serif font-semibold text-primary">
              {t("treatmentPage.whatsIncluded")}
            </div>
            <div className="[&_p]:text-body2">
              <FormattedString>{listing.description}</FormattedString>
            </div>
          </div>
        )}

        {/* Package Items - What's Included */}
        {isPackage && (
          <div className="p-4">
            <div className="text-h4 mb-2 font-serif font-semibold text-primary">
              {t("treatmentPage.whatsIncluded")}
            </div>
            <div className="flex flex-col gap-2">
              {platformUserPackage.platformUserPackageItemModels.map((item) => (
                <PackageItemCard key={item.productId} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Key Treatment Info - Accordion */}
        {hasExpectations && (
          <div className="-mx-2">
            <AccordionList
              items={platformUserPackage.expectations
                .filter((exp) => exp.title && exp.description)
                .map((exp) => ({
                  title: exp.title,
                  children: (
                    <div className="text-justify text-body2 text-primary">{exp.description}</div>
                  ),
                  titleClassName: "font-serif text-h4 font-semibold text-primary",
                }))}
            />
          </div>
        )}

        {/* More Info Link */}
        {showMoreInfoLink && (
          <div className="my-6 flex justify-center">
            <Link to={`/pdp/package/${platformUserPackage.packageListingId}`}>
              <TextButton endIconSVG={ChevronRight}>
                {isPackage
                  ? t("treatmentPage.morePackageInfo")
                  : t("treatmentPage.moreTreatmentInfo")}
              </TextButton>
            </Link>
          </div>
        )}
      </div>

      <ActionModal
        open={activeModal === "redeemSuccess"}
        size="small"
        description={
          isAdminImpersonation ? (
            ""
          ) : (
            <div className="px-2">{t("treatmentPage.redeemSuccessDescription")}</div>
          )
        }
        firstButtonLabel={t("common.close")}
        headerIconSvg={AddedToCartCheck}
        onClose={closeRedeemSuccessModal}
        onFirstButtonClick={closeRedeemSuccessModal}
        title={t("treatmentPage.treatmentRedeemedTitle")}
      />

      {activeModal === "treatmentSelector" && (
        <TreatmentSelectorModal
          primaryOnClick={handleTreatmentSelectorOnClick}
          primaryLabel={treatmentSelectorModalButtonText}
          onClose={hideActiveModal}
          treatments={availableTreatments}
        />
      )}

      {activeModal === "treatmentQuantity" && (
        <QuantitySelectorModal
          primaryOnClick={handleTreatmentQuantityOnClick}
          primaryLabel={quantityModalLabel}
          onClose={hideActiveModal}
          treatment={treatmentDetails}
        />
      )}

      {cost !== null && cost > 0 && isPackageAvailable && listing && activeModal === "buyAgain" && (
        <BuyAgainModal
          open={activeModal === "buyAgain"}
          onClose={hideActiveModal}
          title={platformUserPackage.variantName ?? ""}
          onClickDropdownSelector={() => setActiveModal("quantity")}
          price={price}
          package={$package}
          platform={platform}
          platformUserPackage={platformUserPackage}
          cost={cost}
          listing={listing}
          isPriceSet={!!state.priceId}
          onClickAddToCart={async () => {
            if (!$package || !price) {
              return;
            }

            await createOrderLineItem({
              packageListingId: listing.id,
              packageId: $package.id,
              packagePriceId: price.id,
            });

            queryClient.invalidateQueries({
              queryKey: getListCartSummaryQueryKey({}),
            });

            dispatch({ type: "open", modal: "cart" });
            setActiveModal(null);
          }}
        />
      )}

      {isPackageAvailable && activeModal === "quantity" && listing && $package && (
        <ModalSelectorList
          size="medium"
          open={true}
          onClose={hideActiveModal}
          title={t("treatmentPage.numberOfUnits", {
            unit:
              $package?.packageUnitType.pluralUnitType?.toUpperCase() ??
              t("treatmentPage.unitsFallbackUppercase"),
          })}
          type="products"
          items={$package.packagePrices.map(({ id, price, membershipPrice, quantity }) => ({
            id: id,
            priceOrResults: price,
            membershipPrice: membershipPrice,
            title:
              quantity === 1
                ? `${quantity} ${$package?.packageUnitType.singularUnitType ?? t("treatmentPage.unitFallback")}`
                : `${quantity} ${$package?.packageUnitType.pluralUnitType ?? t("treatmentPage.unitsFallback")}`,
          }))}
          checkedItems={state.priceId ? [state.priceId] : []}
          onSelect={(priceId) => {
            dispatch({ type: "price", priceId });
            previousSelectedAmount = null;
          }}
          firstButtonLabel={t("common.addToCart")}
          onFirstButtonClick={async (locationId?: string) => {
            if (!$package || !price) {
              return;
            }

            await createOrderLineItem({
              packageListingId: listing.id,
              packageId: $package.id,
              packagePriceId: price.id,
              ...(locationId && { locationId }),
            });
            invalidateCurrentOrderDetails();

            dispatch({ type: "open", modal: "cart" });
            setActiveModal(null);
          }}
        />
      )}

      {isPackageAvailable && $package && price && (
        <AddedToCartModal
          title={$package.name}
          open={state.modal === "cart"}
          subtitle={
            price.quantity === 1
              ? `${price.quantity} ${$package.packageUnitType.singularUnitType}`
              : `${price.quantity} ${$package.packageUnitType.pluralUnitType}`
          }
          cost={price.price}
          membership={null}
          onClose={() => {
            dispatch({ type: "close" });
            setActiveModal(null);
          }}
        />
      )}

      {activeModal === "location" && (
        <ModalSelectorList
          size="medium"
          open={true}
          onClose={() => {
            hideActiveModal();
            // Clean up redemption state
            setSelectedQuantity(null);
            setSelectedLocationId(null);
          }}
          title={t("treatmentPage.selectLocationTitle")}
          type="location"
          items={availableLocations.map((location) => ({
            id: location.id,
            title: location.name,
          }))}
          checkedItems={selectedLocationId ? [selectedLocationId] : []}
          onSelect={setSelectedLocationId}
          firstButtonLabel={t("treatmentPage.redeemNow")}
          onFirstButtonClick={() => {
            if (!selectedLocationId) return;
            handleRedeemWithLocation(selectedLocationId);
            setActiveModal(null);
          }}
        />
      )}
    </div>
  );
};
